<template>
  <Dialog
    :header="$t('chat.chat_with_driver')"
    :visible="visible"
    @update:visible="close"
    :style="{ width: '460px', maxWidth: '95vw' }"
    :modal="true"
  >
    <div class="chat-wrap">
      <div class="chat-messages" ref="scroller">
        <div v-if="loading && !messages.length" class="chat-empty">
          {{ $t("chat.loading") }}
        </div>
        <div v-else-if="!messages.length" class="chat-empty">
          {{ $t("chat.no_messages") }}
        </div>
        <div
          v-for="m in messages"
          :key="m.id"
          class="chat-row"
          :class="{ mine: m.sender_type === 'business' }"
        >
          <div
            class="chat-bubble"
            :class="
              m.sender_type === 'business' ? 'bubble-mine' : 'bubble-them'
            "
          >
            <div class="chat-meta" v-if="m.sender_type !== 'business'">
              {{ m.sender_name || captainName }}
            </div>
            <div class="chat-body">{{ m.body }}</div>
            <div class="chat-time">{{ formatTime(m.created_at) }}</div>
          </div>
        </div>
      </div>

      <div v-if="loadFailed" class="chat-closed chat-error">
        {{ $t("chat.load_failed") }}
      </div>
      <div v-else-if="!roomActive" class="chat-closed">
        {{ $t("chat.conversation_closed") }}
      </div>
      <form v-else class="chat-input" @submit.prevent="send">
        <input
          v-model="draft"
          type="text"
          :placeholder="$t('chat.placeholder')"
          :disabled="sending"
          maxlength="2000"
        />
        <button type="submit" :disabled="sending || !draft.trim()">
          <i class="pi pi-send"></i>
        </button>
      </form>
    </div>
  </Dialog>
</template>

<script>
import axios from "axios";
import { formatCompanyTime } from "@/utils/companyTime";
import Dialog from "primevue/dialog";

export default {
  name: "ChatDialog",
  components: { Dialog },
  props: {
    visible: { type: Boolean, default: false },
    bookingId: { required: true },
    captainName: { type: String, default: "" },
  },
  data() {
    return {
      messages: [],
      draft: "",
      loading: false,
      sending: false,
      roomActive: true,
      loadFailed: false,
      poller: null,
    };
  },
  computed: {
    api() {
      return this.$store.state.rootAPI;
    },
    lastId() {
      return this.messages.length
        ? this.messages[this.messages.length - 1].id
        : 0;
    },
  },
  watch: {
    visible(open) {
      if (open) {
        this.messages = [];
        this.loadFailed = false;
        this.load();
        this.startPolling();
      } else {
        this.stopPolling();
      }
    },
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    load() {
      this.loading = true;
      axios
        .get(`${this.api}booking/${this.bookingId}/business-chat`)
        .then((res) => {
          const d = (res.data && res.data.data) || {};
          this.roomActive = d.room ? d.room.active : true;
          this.messages = d.messages || [];
          this.loadFailed = false;
          this.scrollDown();
        })
        .catch((error) => {
          // A definitive refusal (403/404) will not change on retry — show
          // the error banner and stop hammering the API every 4 seconds.
          this.loadFailed = true;
          if (this.isDefinitive(error)) this.stopPolling();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    poll() {
      if (!this.roomActive || this.loadFailed) return;
      axios
        .get(`${this.api}booking/${this.bookingId}/business-chat`, {
          params: { after_id: this.lastId },
        })
        .then((res) => {
          const d = (res.data && res.data.data) || {};
          if (d.room) this.roomActive = d.room.active;
          if (d.messages && d.messages.length) {
            this.messages.push(...d.messages);
            this.scrollDown();
          }
        })
        .catch((error) => {
          if (this.isDefinitive(error)) {
            this.loadFailed = true;
            this.stopPolling();
          }
        });
    },
    isDefinitive(error) {
      const status = error && error.response && error.response.status;
      return status === 403 || status === 404;
    },
    send() {
      const body = this.draft.trim();
      if (!body || this.sending) return;
      this.sending = true;
      axios
        .post(`${this.api}booking/${this.bookingId}/business-chat`, { body })
        .then((res) => {
          const msg = res.data && res.data.data;
          if (msg) this.messages.push(msg);
          this.draft = "";
          this.scrollDown();
        })
        .catch(() => {
          // Ride likely ended -> reload so the closed banner shows.
          this.load();
        })
        .finally(() => {
          this.sending = false;
        });
    },
    startPolling() {
      this.stopPolling();
      this.poller = setInterval(this.poll, 4000);
    },
    stopPolling() {
      if (this.poller) {
        clearInterval(this.poller);
        this.poller = null;
      }
    },
    scrollDown() {
      this.$nextTick(() => {
        const el = this.$refs.scroller;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    // Final-version doc 2026-10-05: Damascus time, whatever the PC zone.
    formatTime(dt) {
      return formatCompanyTime(dt, "HH:mm", "");
    },
    close() {
      this.$emit("update:visible", false);
    },
  },
};
</script>

<style scoped>
.chat-wrap {
  display: flex;
  flex-direction: column;
  height: 60vh;
  max-height: 520px;
}
.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 8px 4px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.chat-empty {
  color: #737780;
  text-align: center;
  padding: 24px 8px;
  font-size: 14px;
}
.chat-row {
  display: flex;
  justify-content: flex-start;
}
.chat-row.mine {
  justify-content: flex-end;
}
.chat-bubble {
  max-width: 78%;
  padding: 8px 12px;
  border-radius: 12px;
}
.bubble-mine {
  background: #54b367;
  color: #fff;
}
.bubble-them {
  background: #f1f1f1;
  color: #241f20;
}
.chat-meta {
  font-size: 11px;
  opacity: 0.75;
  margin-bottom: 2px;
}
.chat-body {
  font-size: 14px;
  white-space: pre-wrap;
  word-break: break-word;
}
.chat-time {
  font-size: 10px;
  opacity: 0.7;
  margin-top: 2px;
  text-align: end;
}
.chat-closed {
  text-align: center;
  color: #737780;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 8px;
}
.chat-error {
  color: #b3261e;
  background: #fdf1f0;
}
.chat-input {
  display: flex;
  gap: 8px;
  padding-top: 8px;
}
.chat-input input {
  flex: 1;
  border: 1px solid #d0d1d4;
  border-radius: 20px;
  padding: 8px 14px;
  outline: none;
}
.chat-input button {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: none;
  background: #54b367;
  color: #fff;
  cursor: pointer;
}
.chat-input button:disabled {
  opacity: 0.5;
  cursor: default;
}
</style>
